/**
 * Marketplace - browse and filter every open chore
 * ------------------------------------------------
 * The filter bar covers all three things asked for: distance in km, pay rate
 * and category, plus a keyword search and a sort order.
 *
 * The distance filter needs to know where you are. It uses the location saved
 * on your account, or you can share it from this page in one click.
 */

import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, SlidersHorizontal, X, Inbox } from 'lucide-react';

import { choreApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import ChoreCard from '../components/ChoreCard';
import Loader from '../components/Loader';
import EmptyState from '../components/EmptyState';
import { useI18n } from '../hooks/useI18n';

const EMPTY_FILTERS = {
  search: '',
  category: '',
  minPay: '',
  maxPay: '',
  maxDistanceKm: '',
  sort: 'newest',
};

export default function Marketplace() {
  const { t } = useI18n();
  const { user, isStudent } = useAuth();

  const [filters, setFilters] = useState(EMPTY_FILTERS);

  /**
   * Whether the filters beyond the search box are showing.
   *
   * Only does anything on a phone - the CSS ignores it above 640px, where
   * there is room for the lot on one row. On a 375px screen those five boxes
   * were 377px of green before the first job appeared, so somebody arriving
   * to look for work saw a form instead. Search stays out, because that is
   * the one people actually use; the rest is one tap away and says how many
   * are set so nothing is silently narrowing what you are shown.
   */
  const [showAllFilters, setShowAllFilters] = useState(false);
  const [categories, setCategories] = useState([]);
  const [chores, setChores] = useState([]);
  const [total, setTotal] = useState(0);
  // True when no chore matched every word of the search and the server has
  // sent the closest ones instead - see backend/utils/search.js.
  const [looseSearch, setLooseSearch] = useState(false);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  /**
   * Distances are measured from the address on the account, and only when it
   * was found on the map - `fromAddress` (backend/utils/place.js). There used
   * to be a "use my location" button here that shared a browser position for
   * the page to sort by; it is gone, because a fix taken indoors can be a
   * town out and the owner was shown 14 km for a chore next door.
   *
   * Somebody with no confirmed address sees no distances, and the two
   * controls that need one are disabled rather than quietly doing nothing.
   * Settings is where an address is added or corrected.
   */
  const canMeasure = user?.location?.fromAddress === true;

  useEffect(() => {
    choreApi
      .categories()
      .then((data) => setCategories(data.categories))
      .catch(() => setCategories([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const query = { ...filters, page, limit: 12 };
      // Only send coordinates when they did not come from the account itself -
      // and in a header, not the address (see services/api.js).
      const data = await choreApi.browse(query);
      setChores(data.chores);
      setTotal(data.total);
      setPages(data.pages);
      setLooseSearch(Boolean(data.looseSearch));
    } catch (err) {
      setError(err.message);
      setChores([]);
      setLooseSearch(false);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  // Re-run the search whenever the filters change - after a short pause, so we
  // are not firing a request on every single keystroke.
  useEffect(() => {
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [load]);

  const updateFilter = (name, value) => {
    setFilters((current) => {
      const next = { ...current, [name]: value };
      /**
       * A search reads best in "best match" order, so starting one switches
       * the sort to it - but only from the default, and only at the moment
       * the box stops being empty. Somebody who then picks "Newest" on
       * purpose keeps it while they carry on typing. Clearing the box puts
       * "best match" back to "newest", since it means nothing without words.
       */
      if (name === 'search') {
        const wasEmpty = current.search.trim() === '';
        const isEmpty = String(value).trim() === '';
        if (wasEmpty && !isEmpty && current.sort === 'newest') next.sort = 'relevance';
        if (isEmpty && current.sort === 'relevance') next.sort = 'newest';
      }
      return next;
    });
    setPage(1);
  };

  const hasFilters = Object.entries(filters).some(
    ([key, value]) => value !== '' && key !== 'sort'
  );

  // How many of the hidden ones are actually doing something. Shown on the
  // button so a collapsed filter can never quietly narrow the list.
  const hiddenFiltersSet = ['category', 'minPay', 'maxPay', 'maxDistanceKm'].filter(
    (key) => filters[key] !== ''
  ).length;

  return (
    <div className="container section">
      <div className="page-header">
        <h1>{t('marketplaceExtra.heading')}</h1>
        <p>
          {isStudent
            ? t('marketplaceExtra.subtitleStudent')
            : t('marketplaceExtra.subtitleOther')}
        </p>
      </div>

      {/* ---------------- FILTERS ---------------- */}
      <div className="filter-bar">
        <div className="filter-grid">
          {/* `field-wide` is a class rather than an inline grid-column so the
              phone rules can still get at it - an inline style beats any
              stylesheet, and this one was forcing a second column onto a
              screen with room for one, which is what made the filters come
              out as a ragged 170px/124px pair. */}
          <div className="field field-wide">
            <label htmlFor="search">
              <Search size={14} style={{ display: 'inline', verticalAlign: -2 }} />{' '}
              {t('marketplaceFilters.search')}
            </label>
            <input
              id="search"
              type="search"
              value={filters.search}
              onChange={(event) => updateFilter('search', event.target.value)}
              placeholder={t('marketplaceFilters.searchPlaceholder')}
            />
          </div>

          {/* Phones only - hidden by CSS on anything wider. */}
          <button
            type="button"
            className="filter-toggle"
            onClick={() => setShowAllFilters((open) => !open)}
            aria-expanded={showAllFilters}
          >
            <SlidersHorizontal size={16} aria-hidden="true" />
            {showAllFilters ? t('marketplace.fewerFilters') : t('marketplace.moreFilters')}
            {!showAllFilters && hiddenFiltersSet > 0 && (
              <span className="filter-toggle-count">{hiddenFiltersSet}</span>
            )}
          </button>

          {/* `display: contents`, so these five stay ordinary items of the
              grid above rather than becoming a box within a box. The wrapper
              exists only so a phone can hide them all in one go. */}
          <div className={`filter-extra${showAllFilters ? ' is-open' : ''}`}>
          <div className="field">
            <label htmlFor="category">{t('marketplace.category')}</label>
            <select
              id="category"
              value={filters.category}
              onChange={(event) => updateFilter('category', event.target.value)}
            >
              <option value="">{t('marketplace.allCategories')}</option>
              {/* The value stays the English name the database stores; only
                  the label is translated - see the warning in i18n/en.js. */}
              {categories.map((category) => (
                <option key={category} value={category}>
                  {t(`categories.${category}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="minPay">{t('marketplaceFilters.payFrom')}</label>
            <input
              id="minPay"
              type="number"
              min="0"
              value={filters.minPay}
              onChange={(event) => updateFilter('minPay', event.target.value)}
              placeholder={t('marketplaceFilters.any')}
            />
          </div>

          <div className="field">
            <label htmlFor="maxPay">{t('marketplaceFilters.payUpTo')}</label>
            <input
              id="maxPay"
              type="number"
              min="0"
              value={filters.maxPay}
              onChange={(event) => updateFilter('maxPay', event.target.value)}
              placeholder={t('marketplaceFilters.any')}
            />
          </div>

          <div className="field">
            <label htmlFor="maxDistanceKm">{t('marketplaceFilters.within')}</label>
            <select
              id="maxDistanceKm"
              value={filters.maxDistanceKm}
              onChange={(event) => updateFilter('maxDistanceKm', event.target.value)}
              disabled={!canMeasure}
            >
              <option value="">{t('marketplaceFilters.anyDistance')}</option>
              <option value="1">1 km</option>
              <option value="2">2 km</option>
              <option value="5">5 km</option>
              <option value="10">10 km</option>
              <option value="20">20 km</option>
              <option value="50">50 km</option>
            </select>
          </div>

          {/* Wide on a phone only, so the last row is never one lonely box
              against an empty half - and so a wide screen still fits the
              whole bar on one line. */}
          <div className="field field-wide-phone">
            <label htmlFor="sort">{t('marketplace.sort')}</label>
            <select
              id="sort"
              value={filters.sort}
              onChange={(event) => updateFilter('sort', event.target.value)}
            >
              {/* Only offered while there is something to match against. */}
              {filters.search.trim() !== '' && (
                <option value="relevance">{t('marketplace.sortRelevance')}</option>
              )}
              <option value="newest">{t('marketplace.sortNewest')}</option>
              <option value="pay">{t('marketplace.sortPay')}</option>
              <option value="soonest">{t('marketplace.sortSoonest')}</option>
              <option value="distance" disabled={!canMeasure}>
                {t('marketplaceFilters.closestToMe')}
              </option>
            </select>
          </div>
          </div>
        </div>

        {/* Where the "use my location" button was. Somebody signed in with
            no address on file is told where to put one, once, and quietly -
            the two distance controls above are already disabled, and this
            says why. */}
        {user && !canMeasure && (
          <div className="row" style={{ marginTop: 'var(--space-sm)' }}>
            <span className="text-sm text-muted">
              <Link to="/settings">{t('marketplaceFilters.addYourAddress')}</Link>{' '}
              {t('marketplaceFilters.toSortByDistance')}
            </span>
          </div>
        )}

        {hasFilters && (
          <button
            type="button"
            className="btn btn-ghost btn-sm btn-inline"
            onClick={() => {
              setFilters(EMPTY_FILTERS);
              setPage(1);
            }}
            style={{ marginTop: 'var(--space-xs)' }}
          >
            <X size={16} /> {t('marketplaceFilters.clearFilters')}
          </button>
        )}
      </div>

      {/* ---------------- RESULTS ---------------- */}
      <div className="row-between" style={{ marginBottom: 'var(--space-sm)' }}>
        <span className="text-sm text-muted">
          <SlidersHorizontal size={14} style={{ display: 'inline', verticalAlign: -2 }} />{' '}
          {loading ? t('marketplaceExtra.searching') : t('marketplaceExtra.found', { count: total })}
        </span>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Said out loud, so a list of near misses is not mistaken for exactly
          what was asked for. */}
      {!loading && looseSearch && chores.length > 0 && (
        <div className="alert alert-info search-loose-note">
          <Search size={18} aria-hidden="true" />
          <div className="text-sm">{t('marketplaceExtra.looseSearch')}</div>
        </div>
      )}

      {loading ? (
        <Loader label={t('common.findingChores')} />
      ) : chores.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title={t('marketplaceExtra.emptyTitle')}
          message={
            hasFilters
              ? t('marketplaceExtra.emptyFiltered')
              : t('marketplaceExtra.emptyNone')
          }
          action={
            hasFilters ? (
              <button
                type="button"
                className="btn btn-outline btn-inline"
                onClick={() => setFilters(EMPTY_FILTERS)}
              >
                {t('marketplaceFilters.clearFilters')}
              </button>
            ) : null
          }
        />
      ) : (
        <>
          <div className="grid-cards">
            {chores.map((chore) => (
              <ChoreCard key={chore._id} chore={chore} invitesBackup />
            ))}
          </div>

          {pages > 1 && (
            <div className="row" style={{ justifyContent: 'center', marginTop: 'var(--space-lg)' }}>
              <button
                type="button"
                className="btn btn-outline btn-sm btn-inline"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                {t('marketplaceFilters.previous')}
              </button>
              <span className="text-sm text-muted" style={{ padding: '0 var(--space-sm)' }}>
                {t('marketplaceFilters.pageOf', { page, pages })}
              </span>
              <button
                type="button"
                className="btn btn-outline btn-sm btn-inline"
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
                disabled={page === pages}
              >
                {t('marketplaceFilters.next')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
